/**
 * 画布缩略图预览：与主画布同步渲染同一份 JSON 数据，仅 scale 不同，无交互。
 * 根及所有后代使用 pointer-events: none，使点击穿透到缩略图栏的按钮，实现「点击缩略图换页」。
 * （仅根节点 pointer-events:none 时，子元素默认 auto 仍会命中并 stopPropagation，故用选择器覆盖整棵子树。）
 */

import { cn } from '@heroui/react';
import type { Page, GroupElement as GroupElementType } from '@/components/canvas/document/publicTypes';
import { GroupElement } from '@/components/canvas/elements/registry/elementComponents';
import { getBuiltinElementComponents } from '@/components/canvas/elements/registry/builtinElements';
import { BlockView } from '@/components/canvas/blocks/BlockView';
import '@/components/canvas/blocks';

const THUMB_PREVIEW_CLASS = 'page-thumbnail-preview';

export interface CanvasThumbnailPreviewProps {
  page: Page;
  /** 容器宽度（如 16:9 下 60*16/9） */
  width: number;
  /** 容器高度（如 60） */
  height: number;
  className?: string;
  /** contain：完整显示；cover：铺满容器（可裁切） */
  fit?: 'contain' | 'cover';
}

const noop = () => {};

export function CanvasThumbnailPreview({
  page,
  width,
  height,
  className,
  fit = 'contain',
}: CanvasThumbnailPreviewProps) {
  const elementComponents = getBuiltinElementComponents();
  const { size, background, elements, blocks = [] } = page;
  const scaleBase = width / size.width || 0.01;
  const scaleAlt = height / size.height || 0.01;
  const scale =
    fit === 'cover'
      ? Math.max(scaleBase, scaleAlt)
      : Math.min(scaleBase, scaleAlt) || 0.01;

  const bgStyle: React.CSSProperties = {};
  if (background.type === 'color') {
    bgStyle.backgroundColor = background.value ?? '#ffffff';
  } else if (background.type === 'gradient' && background.value) {
    bgStyle.background = background.value;
  } else if (background.type === 'transparent') {
    bgStyle.backgroundColor = 'transparent';
  } else {
    bgStyle.backgroundColor = '#ffffff';
  }

  const rootElements = elements.filter((el) => !el.layout.parentId);
  const sorted = rootElements.slice().sort((a, b) => a.layout.zIndex - b.layout.zIndex);
  const sortedBlocks = blocks.slice().sort((a, b) => a.layout.zIndex - b.layout.zIndex);

  const scaledW = size.width * scale;
  const scaledH = size.height * scale;
  const offsetX = (width - scaledW) / 2;
  const offsetY = (height - scaledH) / 2;

  return (
    <>
      <style>{`.${THUMB_PREVIEW_CLASS}, .${THUMB_PREVIEW_CLASS} * { pointer-events: none !important; }`}</style>
      <div
        className={cn(THUMB_PREVIEW_CLASS, className)}
        style={{
          width,
          height,
          overflow: 'hidden',
          position: 'relative',
          backgroundColor: 'var(--surface-secondary)',
        }}
      >
      <div
        style={{
          width: size.width,
          height: size.height,
          transform: `scale(${scale})`,
          transformOrigin: '0 0',
          position: 'absolute',
          left: offsetX,
          top: offsetY,
          ...bgStyle,
        }}
      >
        {background.type === 'image' && background.value && (
          <div
            className="absolute inset-0 bg-cover bg-no-repeat pointer-events-none"
            style={{
              backgroundImage: `url(${background.value})`,
              backgroundColor: background.backgroundColor ?? '#fff',
              backgroundPosition: `${background.imagePositionX ?? 50}% ${background.imagePositionY ?? 50}%`,
            }}
          />
        )}
        <div className="absolute inset-0 pointer-events-none" aria-hidden>
          {sorted.map((el) => {
            if (el.type === 'group') {
              return (
                <GroupElement
                  key={el.id}
                  element={el as GroupElementType}
                  selected={false}
                  dragOffset={{ dx: 0, dy: 0 }}
                  onSelect={noop}
                  onMouseDown={undefined}
                  allElements={elements}
                  selectElement={noop}
                />
              );
            }
            const Comp = elementComponents[el.type as keyof typeof elementComponents];
            if (!Comp) return null;
            return (
              <Comp
                key={el.id}
                element={el as any}
                selected={false}
                dragOffset={{ dx: 0, dy: 0 }}
                onSelect={noop}
                onMouseDown={undefined}
              />
            );
          })}
          {sortedBlocks.map((block) => {
            const { layout } = block;
            return (
              <BlockView
                key={block.id}
                block={block}
                displayLayout={{
                  x: layout.x,
                  y: layout.y,
                  width: layout.width,
                  height: layout.height,
                }}
              />
            );
          })}
        </div>
      </div>
    </div>
    </>
  );
}
